import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
    AssignContentsToPlaylistDto,
    AssignPlaylistLicensesDto,
    BulkDeletePlaylistContentsDto,
    PlaylistDto,
} from '@core/dto';
import { BaseQueryParams } from '@shared';
import { buildQueryParams } from '@shared/utils';
import { Observable } from 'rxjs';
import { ApiGetAllResponse, ApiSingleResponse } from '../interfaces';
import { ApiPlaylist, ApiPlaylistStats, ApiSinglePlaylist } from './playlists.interface';

@Injectable({
    providedIn: 'root',
})
export class PlaylistsService {
    /** Injected http service for api calls */
    private readonly _http = inject(HttpClient);

    /** Base endpoint for the playlists routes */
    private readonly _basePlaylistEndpoint = '/api/playlists';

    /**
     * Retrieves overall playlists stats
     */
    public getPlaylistsStats(): Observable<ApiSingleResponse<ApiPlaylistStats>> {
        return this._http.get<ApiSingleResponse<ApiPlaylistStats>>(
            `${this._basePlaylistEndpoint}/stats`,
        );
    }

    /**
     * Retrieves a paginated list of playlists.
     *
     * @param params - Optional query parameters for pagination, sorting, and filtering
     *
     * @returns Observable of paginated playlists response
     */
    public getPlaylists(params?: BaseQueryParams) {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiGetAllResponse<ApiPlaylist>>(`${this._basePlaylistEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Creates a new playlist using the provided configuration data.
     *
     * @param playlistData - The data transfer object containing the necessary details to construct the new playlist.
     * @returns An `Observable` that emits the result of the creation request from the server.
     */
    public createPlaylist(playlistData: PlaylistDto): Observable<any> {
        return this._http.post<any>(this._basePlaylistEndpoint, playlistData);
    }

    /**
     * Search playlist by dealer name, the API currently does not support OR operation for multiple filters
     *
     * @param searchKey - Dealer name to search for
     *
     * @returns Observable of paginated playlists response
     */
    public searchPlaylistByDealerName(searchKey: string) {
        const queryParams = buildQueryParams({
            filters: { playlistName: { _ilike: searchKey } },
        });
        return this._http.get<ApiGetAllResponse<ApiPlaylist>>(`${this._basePlaylistEndpoint}`, {
            params: queryParams,
        });
    }

    /**
     * Retrieves playlist by its id.
     *
     * @param playlistId - Playlist identifier to retrieve
     * @param params - Optional query parameters (e.g. filters for nested contents)
     *
     * @returns Observable of the playlist response
     */
    public getPlaylistById(
        playlistId: string,
        params?: BaseQueryParams,
    ): Observable<ApiSingleResponse<ApiSinglePlaylist>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiSingleResponse<ApiSinglePlaylist>>(
            `${this._basePlaylistEndpoint}/${playlistId}`,
            { params: queryParams },
        );
    }

    /**
     * Retrieves the contents of a playlist by its identifier.
     *
     * @param playlistId - Playlist identifier whose contents to retrieve
     * @param params - Optional query parameters for pagination, sorting, and filtering
     *
     * @returns Observable of the playlist response. `data` is the full playlist
     * object with paginated `playlistContents` nested inside; `meta.pagination`
     * describes the page slice of those contents.
     */
    public getPlaylistContents(
        playlistId: string,
        params?: BaseQueryParams,
    ): Observable<ApiSingleResponse<ApiSinglePlaylist>> {
        const queryParams = buildQueryParams(params);
        return this._http.get<ApiSingleResponse<ApiSinglePlaylist>>(
            `${this._basePlaylistEndpoint}/${playlistId}/contents`,
            { params: queryParams },
        );
    }

    /**
     * Deletes playlist by its playlist identifier
     *
     * @param playlistId - Playlist identifier to delete
     *
     * @returns Observable of the delete playlist response
     */
    public deletePlaylistById(playlistId: string): Observable<any> {
        return this._http.delete(`${this._basePlaylistEndpoint}/${playlistId}`);
    }

    /**
     * Assigns licenses to every content item of the given playlist.
     *
     * @param playlistId - UUID of the playlist whose contents receive licenses
     * @param payload - Object containing the license identifiers to assign
     *
     * @returns Observable of the assignment response
     */
    public assignLicensesToPlaylist(
        playlistId: string,
        payload: AssignPlaylistLicensesDto,
    ): Observable<any> {
        return this._http.post<any>(`${this._basePlaylistEndpoint}/${playlistId}/license`, payload);
    }

    /**
     * Bulk deletes playlist content items from the given playlist.
     *
     * @param playlistId - UUID of the playlist whose contents are being removed
     * @param payload - Object containing the playlist content identifiers to delete
     *
     * @returns Observable of the bulk delete response
     */
    public bulkDeletePlaylistContents(
        playlistId: string,
        payload: BulkDeletePlaylistContentsDto,
    ): Observable<ApiSingleResponse<ApiSinglePlaylist>> {
        return this._http.post<ApiSingleResponse<ApiSinglePlaylist>>(
            `${this._basePlaylistEndpoint}/${playlistId}/contents/bulk-delete`,
            payload,
        );
    }

    /**
     * Assigns multiple media contents or channels to a playlist.
     *
     * @param playlistId - UUID of the playlist to assign contents to
     * @param payload - Object containing an array of contentIds with their type
     *
     * @returns Observable of the assignment response
     */
    public assignContentsToPlaylist(
        playlistId: string,
        payload: AssignContentsToPlaylistDto,
    ): Observable<ApiSingleResponse<ApiSinglePlaylist>> {
        return this._http.post<ApiSingleResponse<ApiSinglePlaylist>>(
            `${this._basePlaylistEndpoint}/${playlistId}/contents`,
            payload,
        );
    }
}
